// src/pages/WorldExplorer.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Snackbar,
  Tabs,
  Tab,
  Chip,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Language as GalaxyIcon,
  Science as StarIcon,
  Park as PlanetIcon,
  Groups as RaceIcon,
  Flag as FactionIcon,
  Landscape as EnvironmentIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';

import SafeJsonView from '../components/world_explorer/SafeJsonView';
import PropertiesView from '../components/world_explorer/PropertiesView';
import PlanetDetails from '../components/world_explorer/PlanetDetails';
import GalaxyDetails from '../components/world_explorer/GalaxyDetails';
import WorldStructureCard from '../components/world_explorer/WorldStructureCard';
import CustomTreeView from '../components/CustomTreeView';
import EntityDetailsModal from '../components/world_explorer/EntityDetailsModal';
import StarsList from '../components/world_explorer/StarsList';
import StarDetails from '../components/world_explorer/StarDetails';
import WorldCreationModal from '../components/world_explorer/WorldCreationModal';
import WorldGateway from '../api/worldGateway';

// Common layout
import PageShell from '../components/common/PageShell';
import PagePanel from '../components/common/PagePanel';
import StatCard from '../components/common/StatCard';

// RBAC
import { isAdmin } from '../utils/accessControl';

// ============================================================================
// Константы: типы объектов для eager-загрузки
// ============================================================================
const WORLD_OBJECT_TYPES = [
  { key: 'galaxies',     label: 'Галактики',   icon: <GalaxyIcon fontSize="small" />,       color: 'primary',   tab: 2 },
  { key: 'star_systems', label: 'Звёзды',      icon: <StarIcon fontSize="small" />,         color: 'warning',   tab: 1 },
  { key: 'planets',      label: 'Планеты',     icon: <PlanetIcon fontSize="small" />,       color: 'success',   tab: 3 },
  { key: 'environments', label: 'Окружения',   icon: <EnvironmentIcon fontSize="small" />,  color: 'info',      tab: 4 },
  { key: 'races',        label: 'Расы',        icon: <RaceIcon fontSize="small" />,         color: 'secondary', tab: 5 },
  { key: 'factions',     label: 'Фракции',     icon: <FactionIcon fontSize="small" />,      color: 'error',     tab: 6 },
];

const TAB = {
  STRUCTURE: 0,
  STARS: 1,
  GALAXIES: 2,
  PLANETS: 3,
  ENVIRONMENTS: 4,
  RACES: 5,
  FACTIONS: 6,
};

// ============================================================================
// Хелперы
// ============================================================================
const emptyCollections = () => ({
  galaxies: [],
  star_systems: [],
  planets: [],
  environments: [],
  races: [],
  factions: [],
});

const initialLoadingByType = () =>
  WORLD_OBJECT_TYPES.reduce((acc, t) => ({ ...acc, [t.key]: 'idle' }), {});

const initialErrorsByType = () =>
  WORLD_OBJECT_TYPES.reduce((acc, t) => ({ ...acc, [t.key]: null }), {});

// ============================================================================
// Главный компонент
// ============================================================================
export default function WorldExplorer() {
  const auth = useSelector((state) => state.auth);
  const isAuthenticated = auth?.isAuthenticated;

  // 🛡️ Управлять мирами (создавать/удалять/reload/start/pause) может только admin/superadmin
  const canManageWorlds = isAdmin(auth);

  // --- Мир / данные ---
  const [availableWorlds, setAvailableWorlds] = useState([]);
  const [currentWorld, setCurrentWorld] = useState(
    () => localStorage.getItem('current_world') || ''
  );
  const [worldData, setWorldData] = useState(null);
  const [worldDetails, setWorldDetails] = useState(null);

  const [collections, setCollections] = useState(emptyCollections());
  const [loadingByType, setLoadingByType] = useState(initialLoadingByType());
  const [errorsByType, setErrorsByType] = useState(initialErrorsByType());

  const [loading, setLoading] = useState(false);
  const [loadingWorlds, setLoadingWorlds] = useState(false);

  // --- UI ---
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [activeTab, setActiveTab] = useState(TAB.STRUCTURE);
  const [detailTab, setDetailTab] = useState(0);

  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodePath, setSelectedNodePath] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // --- Модалки ---
  const [entityModal, setEntityModal] = useState({ open: false, entity: null, entityType: null });
  const [creationModal, setCreationModal] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    worldId: null,
    worldName: '',
  });

  // --- Меню мира ---
  const [worldMenuAnchor, setWorldMenuAnchor] = useState(null);
  const [selectedWorldId, setSelectedWorldId] = useState(null);

  // --- Защита от гонок при переключении мира ---
  const requestEpochRef = useRef(0);

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // ==========================================================================
  // Загрузка списка миров
  // ==========================================================================
  const loadAvailableWorlds = useCallback(async () => {
    if (!isAuthenticated) {
      showSnackbar('Authentication required', 'warning');
      return;
    }
    setLoadingWorlds(true);
    try {
      const worlds = await WorldGateway.getAvailableWorlds();
      const sorted = [...worlds].sort((a, b) => {
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (a.status !== 'active' && b.status === 'active') return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setAvailableWorlds(sorted);

      if (!currentWorld && sorted.length > 0) {
        const activeWorld = sorted.find((w) => w.status === 'active');
        setCurrentWorld(activeWorld?.id || sorted[0].id);
      }
      showSnackbar(`Loaded ${sorted.length} worlds`, 'success');
    } catch (error) {
      console.error('❌ Failed to load available worlds:', error);
      showSnackbar(`Error loading worlds: ${error.message}`, 'error');
    } finally {
      setLoadingWorlds(false);
    }
  }, [isAuthenticated, currentWorld, showSnackbar]);

  // ==========================================================================
  // Eager-загрузка мира
  // ==========================================================================
  const loadWorldData = useCallback(
    async (worldId) => {
      if (!isAuthenticated) {
        showSnackbar('Authentication required to load world data', 'warning');
        return;
      }

      const epoch = ++requestEpochRef.current;

      setLoading(true);
      setLoadingByType(initialLoadingByType());
      setErrorsByType(initialErrorsByType());
      setCollections(emptyCollections());

      setSelectedNode(null);
      setSelectedNodePath('');
      setSearchTerm('');
      setDetailTab(0);
      setActiveTab(TAB.STRUCTURE);

      try {
        const details = await WorldGateway.getWorldDetails(worldId);
        if (epoch !== requestEpochRef.current) return;
        setWorldDetails(details);
        setWorldData({
          id: worldId,
          name: details.name || worldId,
          type: details.type || 'unknown',
          description: details.description || 'World data',
          status: details.status || 'inactive',
          config: details.config || {},
          metadata: details.metadata || {},
          statistics: details.statistics || {},
        });
        setCurrentWorld(worldId);
        localStorage.setItem('current_world', worldId);

        setLoadingByType((prev) =>
          WORLD_OBJECT_TYPES.reduce((acc, t) => ({ ...acc, [t.key]: 'loading' }), prev)
        );

        const results = await Promise.allSettled(
          WORLD_OBJECT_TYPES.map((t) => WorldGateway.getWorldObjects(worldId, t.key))
        );
        if (epoch !== requestEpochRef.current) return;

        const nextCollections = emptyCollections();
        const nextLoading = initialLoadingByType();
        const nextErrors = initialErrorsByType();

        results.forEach((res, idx) => {
          const t = WORLD_OBJECT_TYPES[idx];
          if (res.status === 'fulfilled') {
            nextCollections[t.key] = Array.isArray(res.value) ? res.value : [];
            nextLoading[t.key] = 'ok';
          } else {
            nextCollections[t.key] = [];
            nextLoading[t.key] = 'error';
            nextErrors[t.key] = res.reason?.message || 'unknown';
            console.warn(`⚠️ ${t.key}:`, res.reason);
          }
        });

        setCollections(nextCollections);
        setLoadingByType(nextLoading);
        setErrorsByType(nextErrors);

        const totalLoaded = Object.values(nextCollections).reduce(
          (sum, arr) => sum + arr.length,
          0
        );
        showSnackbar(`World "${details.name}" loaded (${totalLoaded} objects)`, 'success');
      } catch (err) {
        if (epoch !== requestEpochRef.current) return;
        console.error('💥 Error loading world:', err);
        showSnackbar(`Error loading world: ${err.message}`, 'error');
      } finally {
        if (epoch === requestEpochRef.current) setLoading(false);
      }
    },
    [isAuthenticated, showSnackbar]
  );

  // ==========================================================================
  // Действия с мирами
  // ==========================================================================
  const handleWorldSelect = useCallback(
    (worldId) => {
      if (!isAuthenticated) {
        showSnackbar('Please log in to load worlds', 'error');
        return;
      }
      if (worldId === currentWorld && worldData) return;
      loadWorldData(worldId);
    },
    [isAuthenticated, currentWorld, worldData, loadWorldData, showSnackbar]
  );

  const handleWorldMenuOpen = (event, worldId) => {
    if (!canManageWorlds) {
      showSnackbar('Недостаточно прав', 'warning');
      return;
    }
    setWorldMenuAnchor(event.currentTarget);
    setSelectedWorldId(worldId);
  };

  const handleWorldMenuClose = () => {
    setWorldMenuAnchor(null);
    setSelectedWorldId(null);
  };

  const handleWorldAction = async (action) => {
    if (!canManageWorlds) {
      showSnackbar('Недостаточно прав', 'warning');
      handleWorldMenuClose();
      return;
    }
    if (!selectedWorldId) return;

    try {
      switch (action) {
        case 'reload':
          await loadWorldData(selectedWorldId);
          showSnackbar('World reloaded', 'success');
          break;
        case 'start':
          await WorldGateway.startWorldSimulation(selectedWorldId);
          showSnackbar('Simulation started', 'success');
          await loadAvailableWorlds();
          break;
        case 'pause':
          await WorldGateway.pauseWorldSimulation(selectedWorldId);
          showSnackbar('Simulation paused', 'success');
          await loadAvailableWorlds();
          break;
        case 'delete':
          setDeleteConfirmDialog({
            open: true,
            worldId: selectedWorldId,
            worldName:
              availableWorlds.find((w) => w.id === selectedWorldId)?.name || selectedWorldId,
          });
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('❌ Error performing world action:', error);
      showSnackbar(`Error: ${error.message}`, 'error');
    } finally {
      handleWorldMenuClose();
    }
  };

  const handleConfirmDelete = async () => {
    if (!canManageWorlds) {
      showSnackbar('Недостаточно прав', 'warning');
      setDeleteConfirmDialog({ open: false, worldId: null, worldName: '' });
      return;
    }
    if (!deleteConfirmDialog.worldId) return;
    try {
      await WorldGateway.deleteWorld(deleteConfirmDialog.worldId);
      showSnackbar(
        `World "${deleteConfirmDialog.worldName}" deleted successfully`,
        'success'
      );

      if (currentWorld === deleteConfirmDialog.worldId) {
        setCurrentWorld('');
        setWorldData(null);
        setWorldDetails(null);
        setCollections(emptyCollections());
        setLoadingByType(initialLoadingByType());
        setErrorsByType(initialErrorsByType());
        localStorage.removeItem('current_world');
      }
      await loadAvailableWorlds();
    } catch (error) {
      console.error('❌ Failed to delete world:', error);
      showSnackbar(`Error deleting world: ${error.message}`, 'error');
    } finally {
      setDeleteConfirmDialog({ open: false, worldId: null, worldName: '' });
    }
  };

  const handleWorldCreated = (newWorld) => {
    setCreationModal(false);
    showSnackbar(`World "${newWorld.name}" created successfully`, 'success');
    loadAvailableWorlds();
    setTimeout(() => loadWorldData(newWorld.id), 500);
  };

  // ==========================================================================
  // Выбор узла в дереве
  // ==========================================================================
  const handleNodeSelect = (path, node) => {
    setSelectedNodePath(path);
    setSelectedNode(node);
    setDetailTab(0);
  };

  const handleEntityOpen = (entity, entityType) => {
    setEntityModal({ open: true, entity, entityType });
  };

  const handleEntityClose = () => {
    setEntityModal({ open: false, entity: null, entityType: null });
  };

  const getSelectedNodeData = () => {
    if (!selectedNodePath || !worldData) return null;
    try {
      const path = selectedNodePath.replace('world.', '').split('.');
      let current = worldData;
      for (const segment of path) {
        if (segment.includes('[')) {
          const match = segment.match(/(.+)\[(\d+)\]/);
          if (match && current) current = current[match[1]]?.[parseInt(match[2], 10)];
        } else {
          current = current?.[segment];
        }
        if (!current) break;
      }
      return current || selectedNode;
    } catch {
      return selectedNode;
    }
  };

  const selectedNodeData = getSelectedNodeData();

  const getNodeDetailsComponent = () => {
    if (!selectedNodeData) return null;

    if (selectedNodeData.galaxy_id || selectedNodePath?.includes('planets')) {
      return (
        <PlanetDetails
          planet={selectedNodeData}
          races={collections.races}
          factions={collections.factions}
          environments={collections.environments}
        />
      );
    }
    if (selectedNodePath?.includes('galaxies')) {
      return (
        <GalaxyDetails
          galaxy={selectedNodeData}
          planets={collections.planets}
          races={collections.races}
          factions={collections.factions}
        />
      );
    }
    if (selectedNodePath?.includes('stars') || selectedNodePath?.includes('star_systems')) {
      return (
        <StarDetails
          star={selectedNodeData}
          planets={collections.planets}
          galaxies={collections.galaxies}
        />
      );
    }
    return <PropertiesView data={selectedNodeData} />;
  };

  // ==========================================================================
  // Эффекты
  // ==========================================================================
  useEffect(() => {
    if (isAuthenticated) loadAvailableWorlds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (!currentWorld) return;
    if (worldDetails?.id === currentWorld) return;
    if (!availableWorlds.some((w) => w.id === currentWorld)) return;
    loadWorldData(currentWorld);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorld, availableWorlds]);

  // ==========================================================================
  // Производные
  // ==========================================================================
  const counts = useMemo(() => {
    return WORLD_OBJECT_TYPES.reduce(
      (acc, t) => ({ ...acc, [t.key]: collections[t.key]?.length || 0 }),
      {}
    );
  }, [collections]);

  const statusColorFor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'creating': return 'warning';
      case 'paused': return 'info';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  // ==========================================================================
  // Render: список миров (компактный, левая колонка)
  // ==========================================================================
  const worldsList = (
    <PagePanel sx={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
          Миры ({availableWorlds.length})
        </Typography>
        <Stack direction="row" spacing={0.25}>
          <Tooltip title="Обновить список">
            <span>
              <IconButton size="small" onClick={loadAvailableWorlds} disabled={loadingWorlds}>
                {loadingWorlds ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          {canManageWorlds && (
            <Tooltip title="Создать мир">
              <IconButton size="small" color="primary" onClick={() => setCreationModal(true)}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {loadingWorlds && availableWorlds.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : availableWorlds.length === 0 ? (
          <Box sx={{ p: 1.5 }}>
            <Alert severity="info" sx={{ fontSize: '0.7rem' }}>
              Миров нет.
              {canManageWorlds ? ' Создайте первый.' : ' Обратитесь к администратору.'}
            </Alert>
          </Box>
        ) : (
          <List dense disablePadding>
            {availableWorlds.map((world) => {
              const isCurrent = currentWorld === world.id;
              return (
                <ListItemButton
                  key={world.id}
                  selected={isCurrent}
                  onClick={() => handleWorldSelect(world.id)}
                  sx={{ py: 0.5, pr: canManageWorlds ? 5 : 1.5, borderLeft: 3, borderColor: isCurrent ? 'primary.main' : 'transparent' }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ fontWeight: isCurrent ? 700 : 500, fontSize: '0.8rem' }}
                        >
                          {world.name}
                        </Typography>
                        <Chip
                          label={world.status}
                          size="small"
                          color={statusColorFor(world.status)}
                          variant="outlined"
                          sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.5rem', px: 0.5 } }}
                        />
                      </Stack>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.6rem' }}>
                        {world.type}
                        {world.statistics?.planet_count != null && ` · ${world.statistics.planet_count} планет`}
                      </Typography>
                    }
                  />
                  {canManageWorlds && (
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleWorldMenuOpen(e, world.id);
                        }}
                      >
                        <MoreIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </PagePanel>
  );

  // ==========================================================================
  // Render: правая панель — табы + контент
  // ==========================================================================
  const rightPanel = (
    <PagePanel sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 34,
            '& .MuiTab-root': {
              minHeight: 34,
              py: 0.25,
              fontSize: '0.7rem',
              textTransform: 'none',
              minWidth: 'auto',
              px: 1.25,
            },
          }}
        >
          <Tab icon={<CategoryIcon fontSize="small" />} iconPosition="start" label="Структура" />
          <Tab icon={<StarIcon fontSize="small" />} iconPosition="start" label={`Звёзды (${counts.star_systems})`} />
          <Tab icon={<GalaxyIcon fontSize="small" />} iconPosition="start" label={`Галактики (${counts.galaxies})`} />
          <Tab icon={<PlanetIcon fontSize="small" />} iconPosition="start" label={`Планеты (${counts.planets})`} />
          <Tab icon={<EnvironmentIcon fontSize="small" />} iconPosition="start" label={`Окружения (${counts.environments})`} />
          <Tab icon={<RaceIcon fontSize="small" />} iconPosition="start" label={`Расы (${counts.races})`} />
          <Tab icon={<FactionIcon fontSize="small" />} iconPosition="start" label={`Фракции (${counts.factions})`} />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 1 }}>
        {!worldData && (
          <Alert severity="info">
            {isAuthenticated
              ? 'Выберите мир слева или создайте новый.'
              : 'Требуется вход, чтобы загрузить миры.'}
          </Alert>
        )}

        {worldData && activeTab === TAB.STRUCTURE && (
          <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', lg: 'row' } }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label="Поиск в мире..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                fullWidth
                placeholder="Имя, тип или свойство"
                sx={{ mb: 1 }}
              />
              <CustomTreeView
                data={worldData}
                onNodeSelect={handleNodeSelect}
                selectedPath={selectedNodePath}
                height="55vh"
              />
            </Box>
            <Box sx={{ flex: 2, minWidth: 0 }}>
              {selectedNode ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 1, py: 0.25 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                      <strong>{selectedNode.name || 'Unnamed'}</strong>
                      {selectedNode.type && (
                        <Chip label={selectedNode.type} size="small" color="primary" />
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleEntityOpen(selectedNode, selectedNode.type)}
                      >
                        Открыть
                      </Button>
                    </Stack>
                  </Alert>
                  <Tabs
                    value={detailTab}
                    onChange={(_, v) => setDetailTab(v)}
                    sx={{
                      minHeight: 28,
                      mb: 1,
                      '& .MuiTab-root': {
                        minHeight: 28,
                        fontSize: '0.65rem',
                        textTransform: 'none',
                        py: 0.25,
                      },
                    }}
                  >
                    <Tab label="Детали" />
                    <Tab label="Свойства" />
                    <Tab label="JSON" />
                  </Tabs>
                  {detailTab === 0 && getNodeDetailsComponent()}
                  {detailTab === 1 && <PropertiesView data={selectedNodeData} />}
                  {detailTab === 2 && <SafeJsonView data={selectedNodeData} />}
                </Box>
              ) : (
                <WorldStructureCard
                  worldData={worldData}
                  galaxies={collections.galaxies}
                  planets={collections.planets}
                  stars={collections.star_systems}
                  races={collections.races}
                  factions={collections.factions}
                  environments={collections.environments}
                  onEntityOpen={handleEntityOpen}
                />
              )}
            </Box>
          </Box>
        )}

        {worldData && activeTab === TAB.STARS && (
          <StarsList
            stars={collections.star_systems}
            planets={collections.planets}
            onStarSelect={(star) => handleEntityOpen(star, 'star')}
          />
        )}

        {worldData && activeTab === TAB.GALAXIES && (
          <EntityGrid
            items={collections.galaxies}
            loading={loadingByType.galaxies === 'loading'}
            error={errorsByType.galaxies}
            emptyLabel="Нет галактик"
            icon={<GalaxyIcon />}
            onOpen={(g) => handleEntityOpen(g, 'galaxy')}
            renderSubtitle={(g) => `${g.type || 'Galaxy'}${g.diameter_ly ? ` · ${g.diameter_ly} ly` : ''}`}
          />
        )}

        {worldData && activeTab === TAB.PLANETS && (
          <EntityGrid
            items={collections.planets}
            loading={loadingByType.planets === 'loading'}
            error={errorsByType.planets}
            emptyLabel="Нет планет"
            icon={<PlanetIcon />}
            onOpen={(p) => handleEntityOpen(p, 'planet')}
            renderSubtitle={(p) => `${p.type || 'Planet'}${p.system_id ? ` · ${p.system_id}` : ''}`}
            maxItems={24}
          />
        )}

        {worldData && activeTab === TAB.ENVIRONMENTS && (
          <EntityGrid
            items={collections.environments}
            loading={loadingByType.environments === 'loading'}
            error={errorsByType.environments}
            emptyLabel="Нет окружений"
            icon={<EnvironmentIcon />}
            onOpen={(e) => handleEntityOpen(e, 'environment')}
            renderSubtitle={(e) => e.type || 'Environment'}
          />
        )}

        {worldData && activeTab === TAB.RACES && (
          <EntityGrid
            items={collections.races}
            loading={loadingByType.races === 'loading'}
            error={errorsByType.races}
            emptyLabel="Нет рас"
            icon={<RaceIcon />}
            onOpen={(r) => handleEntityOpen(r, 'race')}
            renderSubtitle={(r) => r.description || 'Biological species'}
          />
        )}

        {worldData && activeTab === TAB.FACTIONS && (
          <EntityGrid
            items={collections.factions}
            loading={loadingByType.factions === 'loading'}
            error={errorsByType.factions}
            emptyLabel="Нет фракций"
            icon={<FactionIcon />}
            onOpen={(f) => handleEntityOpen(f, 'faction')}
            renderSubtitle={(f) => f.description || 'Political organization'}
          />
        )}
      </Box>
    </PagePanel>
  );

  // ==========================================================================
  // Render: шапка (счётчики)
  // ==========================================================================
  const headerPanel = (
    <PagePanel sx={{ flexShrink: 0 }}>
      <Box sx={{ py: 1, px: 1.5 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={1}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ fontSize: '1.05rem' }}>
                🪐 UEF World Explorer
              </Typography>
              {worldData && (
                <Chip
                  label={worldData.name}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.65rem' } }}
                />
              )}
              {worldDetails?.status && (
                <Chip
                  label={worldDetails.status}
                  size="small"
                  color={statusColorFor(worldDetails.status)}
                  sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.65rem' } }}
                />
              )}
              {!canManageWorlds && isAuthenticated && (
                <Chip
                  icon={<LockIcon />}
                  label="Только просмотр"
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.6rem' } }}
                />
              )}
              {loading && <CircularProgress size={14} />}
            </Stack>
            {!isAuthenticated && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}>
                Требуется вход для загрузки миров
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
            {WORLD_OBJECT_TYPES.map((t) => (
              <StatCard
                key={t.key}
                compact
                title={t.label}
                value={counts[t.key]}
                color={t.color}
                icon={t.icon}
                loading={loadingByType[t.key] === 'loading'}
              />
            ))}
          </Stack>
        </Stack>
      </Box>
    </PagePanel>
  );

  // ==========================================================================
  // Render: панель действий
  // ==========================================================================
  const actionsPanel = (
    <PagePanel sx={{ flexShrink: 0 }}>
      <Box sx={{ py: 0.75, px: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={loadingWorlds ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon fontSize="small" />}
            onClick={loadAvailableWorlds}
            disabled={loadingWorlds || !isAuthenticated}
            sx={{ fontSize: '0.7rem', py: 0.5 }}
          >
            Обновить список
          </Button>

          {canManageWorlds && (
            <Button
              size="small"
              variant="outlined"
              startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
              onClick={() => currentWorld && loadWorldData(currentWorld)}
              disabled={loading || !currentWorld}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              Перезагрузить мир
            </Button>
          )}

          {canManageWorlds && worldDetails?.status !== 'active' && currentWorld && (
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<PlayArrowIcon fontSize="small" />}
              onClick={() => handleWorldAction('start')}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              Запустить симуляцию
            </Button>
          )}
          {canManageWorlds && worldDetails?.status === 'active' && currentWorld && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<PauseIcon fontSize="small" />}
              onClick={() => handleWorldAction('pause')}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              Пауза
            </Button>
          )}

          <Box sx={{ flex: 1 }} />

          {canManageWorlds && (
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setCreationModal(true)}
              disabled={!isAuthenticated}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              Создать мир
            </Button>
          )}

          {canManageWorlds && currentWorld && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon fontSize="small" />}
              onClick={() =>
                setDeleteConfirmDialog({
                  open: true,
                  worldId: currentWorld,
                  worldName: worldData?.name || currentWorld,
                })
              }
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              Удалить
            </Button>
          )}
        </Stack>
      </Box>
    </PagePanel>
  );

  // ==========================================================================
  // JSX
  // ==========================================================================
  return (
    <PageShell>
      {headerPanel}
      {actionsPanel}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 0.75, overflow: 'hidden', flexDirection: { xs: 'column', md: 'row' } }}>
        {worldsList}
        {rightPanel}
      </Box>

      {/* Меню мира (только для admin) */}
      <Menu
        anchorEl={worldMenuAnchor}
        open={Boolean(worldMenuAnchor)}
        onClose={handleWorldMenuClose}
      >
        <MenuItem onClick={() => handleWorldAction('reload')}>
          <RefreshIcon sx={{ mr: 1, fontSize: 18 }} /> Перезагрузить
        </MenuItem>
        {availableWorlds.find((w) => w.id === selectedWorldId)?.status !== 'active' && (
          <MenuItem onClick={() => handleWorldAction('start')}>
            <PlayArrowIcon sx={{ mr: 1, fontSize: 18 }} /> Запустить
          </MenuItem>
        )}
        {availableWorlds.find((w) => w.id === selectedWorldId)?.status === 'active' && (
          <MenuItem onClick={() => handleWorldAction('pause')}>
            <PauseIcon sx={{ mr: 1, fontSize: 18 }} /> Пауза
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleWorldAction('delete')} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} /> Удалить
        </MenuItem>
      </Menu>

      {/* Диалог удаления */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, worldId: null, worldName: '' })}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DeleteIcon color="error" />
            <span>Удалить мир?</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Мир "{deleteConfirmDialog.worldName}" будет удалён безвозвратно.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Все данные мира будут потеряны. Если в мире есть игроки — они будут отключены.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteConfirmDialog({ open: false, worldId: null, worldName: '' })
            }
          >
            Отмена
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Модалки */}
      <EntityDetailsModal
        open={entityModal.open}
        onClose={handleEntityClose}
        entity={entityModal.entity}
        entityType={entityModal.entityType}
        additionalData={{
          galaxies: collections.galaxies,
          planets: collections.planets,
          stars: collections.star_systems,
          races: collections.races,
          factions: collections.factions,
          environments: collections.environments,
        }}
      />

      <WorldCreationModal
        open={creationModal}
        onClose={() => setCreationModal(false)}
        onWorldCreated={handleWorldCreated}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, message: '', open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </PageShell>
  );
}

// ============================================================================
// Вспомогательный компонент: сетка сущностей
// ============================================================================
function EntityGrid({
  items = [],
  loading = false,
  error = null,
  emptyLabel = 'Нет данных',
  icon = null,
  onOpen,
  renderSubtitle,
  maxItems = null,
}) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">Ошибка загрузки: {String(error)}</Alert>;
  }
  if (!items || items.length === 0) {
    return (
      <Alert severity="info" icon={icon} sx={{ fontSize: '0.75rem' }}>
        {emptyLabel}
      </Alert>
    );
  }

  const visible = maxItems ? items.slice(0, maxItems) : items;

  return (
    <>
      <Grid container spacing={1}>
        {visible.map((item, idx) => (
          <Grid item xs={12} sm={6} md={4} key={item.id || idx}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                <Typography variant="subtitle2" noWrap sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                  {item.name || item.id || 'Unnamed'}
                </Typography>
                {renderSubtitle && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: 'block', fontSize: '0.65rem' }}
                  >
                    {renderSubtitle(item)}
                  </Typography>
                )}
                <Button
                  size="small"
                  onClick={() => onOpen?.(item)}
                  sx={{ mt: 0.5, fontSize: '0.65rem', py: 0, minHeight: 22 }}
                >
                  Подробнее
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {maxItems && items.length > maxItems && (
        <Alert severity="info" sx={{ mt: 1, fontSize: '0.7rem' }}>
          Показаны первые {maxItems} из {items.length}.
        </Alert>
      )}
    </>
  );
}
